import { Link } from "react-router-dom";

export default function RentOrBuyCalculatorGuide() {
    return (
        <>
            <p>
                "Renting is throwing money away." You've heard it. It's not entirely wrong — but it's not right, either. Whether
                buying makes more financial sense than renting depends on your local market, how long you plan to stay, and what
                you'd do with your down payment money otherwise. The math is more nuanced than the bumper sticker, and it's
                worth working through before making a decision that affects your finances for years.
            </p>

            <h2>The True Cost of Buying</h2>
            <p>
                Your mortgage payment isn't the cost of homeownership. It's one piece of it. Here's what you actually pay when
                you own:
            </p>
            <p>
                <strong>Mortgage principal and interest:</strong> The big line item, but part of every payment reduces the
                balance you owe.<br />
                <strong>Property taxes:</strong> Typically 0.5%–2.5% of the home value annually, depending on location. On a
                $350,000 home in a 1.2% tax area, that's $4,200/year or $350/month.<br />
                <strong>Homeowner's insurance:</strong> Roughly $1,000–$2,000/year.<br />
                <strong>Maintenance and repairs:</strong> Budget 1%–2% of home value per year. On a $350,000 home, that's
                $3,500–$7,000 annually. Some years it's zero. Some years the roof or HVAC costs more than that alone.<br />
                <strong>HOA fees</strong> (if applicable): Can range from $0 to $1,000+/month.<br />
                <strong>PMI</strong> (if less than 20% down): Typically $100–$300/month until you hit 20% equity.
            </p>
            <p>
                On a $350,000 home with 10% down at 7% interest: the mortgage payment is about $2,096. Add $350 in taxes,
                $150 in insurance, $350 in maintenance, and $150 in PMI, and you're at $3,096/month all-in — not $2,096.
            </p>

            <h2>The True Cost of Renting</h2>
            <p>
                Renting is simpler. You pay rent. In most cases, utilities, renter's insurance ($15–$30/month), and that's it.
                No maintenance costs, no property tax, no PMI. If the HVAC breaks, your landlord handles it.
            </p>
            <p>
                The "lost" money in renting isn't the full rent payment — it's rent minus what you'd be paying in non-equity-building
                costs as a homeowner (interest, taxes, insurance, maintenance). In the early years of a mortgage, the majority
                of your payment is interest. On a $315,000 loan at 7%, your first monthly payment of $2,096 includes about
                $1,838 in interest and only $258 in principal. The "equity" you're building is only $258.
            </p>

            <h2>The Break-Even Timeline</h2>
            <p>
                Buying a home comes with significant upfront costs: 3%–20% down payment, plus closing costs of 2%–5% of the
                purchase price. On a $350,000 home, closing costs alone might be $7,000–$14,000. You need time for appreciation
                and equity to recoup those costs before buying becomes the better deal.
            </p>
            <p>
                A common rule of thumb: if you're staying fewer than 3–5 years, renting is often cheaper in most markets. The
                longer you stay, the more buying tilts in your favor. Use the{" "}
                <Link to="/calculator/rent-vs-buy">rent vs buy calculator</Link> to find the break-even point for your specific
                numbers — home price, rent alternative, expected appreciation, and planned duration.
            </p>

            <h2>What Happens to Your Down Payment if You Don't Buy</h2>
            <p>
                This is the part the "buying is always better" crowd skips. If you're comparing a $400,000 home purchase to
                renting, you're comparing scenarios where in the buying case you deploy $60,000 upfront (15% down + closing
                costs). If instead you rent and invest that $60,000 in a diversified index fund returning 7% annually, after
                10 years you'd have roughly $118,000.
            </p>
            <p>
                Meanwhile, the $400,000 home at 4% annual appreciation would be worth $592,000. But you've also paid ~$200,000
                in mortgage payments over those 10 years, plus taxes, insurance, and maintenance. The net wealth calculation
                is less obvious than it seems.
            </p>
            <p>
                In high-appreciation markets (historically: coastal cities, some Sun Belt metros), buying wins convincingly over
                10+ year timeframes. In slower-appreciation markets, the math is tighter. In some markets, renting and
                investing the difference genuinely outperforms buying.
            </p>

            <h2>Non-Financial Factors That Matter</h2>
            <p>
                The financial analysis is important but not complete. Homeownership gives you stability, the freedom to
                customize your space, and a forced savings mechanism (building equity through mortgage payments). These are real
                value even if they don't show up in a spreadsheet.
            </p>
            <p>
                Renting gives you flexibility and liquidity. If your job requires relocation, a relationship ends, or your
                city's market changes, you can move with 30–60 days' notice. A homeowner in the same situation may need to sell
                into whatever market exists, paying 5%–6% in agent commissions plus closing costs.
            </p>

            <h2>Questions to Answer Before Deciding</h2>
            <p>
                1. How long will you realistically stay? Less than 3 years is generally renting territory unless the market is
                unusually strong.<br />
                2. What's the price-to-rent ratio in your area? Divide the home price by annual rent for a comparable home. A
                ratio above 20 generally favors renting; below 15 generally favors buying.<br />
                3. Can you afford the all-in cost, not just the mortgage?<br />
                4. What would you do with the down payment if you didn't buy?
            </p>

            <h2>The Bottom Line</h2>
            <p>
                Buying is better sometimes. Renting is better sometimes. The answer depends on your numbers, your market, and
                your timeline — not on a platitude about throwing money away. Run the actual math with the{" "}
                <Link to="/calculator/rent-vs-buy">rent vs buy calculator</Link> before committing.
            </p>
        </>
    );
}